import { CardMedia, Card, CardActionArea, CardActions, CardContent, Stack, Typography, Box, AvatarGroup, Avatar, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ITournamentState } from '../../../../Core/State/ILobbyTournamentState';
import { IUserState } from '../../../../Core/State/IUserState';
import defaultTourneyImg from '/images/tournament/default.png';
import ScheduleIcon from '@mui/icons-material/Schedule';
import {
    isLateRegistrationOpen,
    getEffectivePrize,
    getStateColor,
    getStartText,
    formatCurrencySmartDecimal,
    isTournamentRegisteredByUser
} from '../../../../Utils/TournamentUtils';
import { t } from '../../../../i18n';
import avatar1 from '/images/avatar/default-avatar-1.jpg';
import avatar2 from '/images/avatar/default-avatar-2.jpg';
import avatar3 from '/images/avatar/default-avatar-3.jpg';
import avatar4 from '/images/avatar/default-avatar-4.jpg';
import avatar5 from '/images/avatar/default-avatar-5.jpg';
import avatar6 from '/images/avatar/default-avatar-6.jpg';
import avatar7 from '/images/avatar/default-avatar-7.jpg';
import avatar8 from '/images/avatar/default-avatar-8.jpg';
import avatar9 from '/images/avatar/default-avatar-9.jpg';
import avatar10 from '/images/avatar/default-avatar-10.jpg';
import avatar11 from '/images/avatar/default-avatar-11.jpg';
import avatar12 from '/images/avatar/default-avatar-12.jpg';
import freerollIcon from '/images/lobby/freeroll.svg';
import satelliteIcon from '/images/lobby/satellite.svg';

const defaultAvatars = [avatar1, avatar2, avatar3, avatar4, avatar5, avatar6, avatar7, avatar8, avatar9, avatar10, avatar11, avatar12];

// Get a consistent random avatar for a given userId
const getDefaultAvatar = (userId: string | number): string => {
    const id = typeof userId === 'string' ? userId : userId.toString();
    let hash = 0;
    for (let i = 0; i < id.length; i++) {
        hash = ((hash << 5) - hash) + id.charCodeAt(i);
        hash = hash & hash; // Convert to 32-bit integer
    }
    const index = Math.abs(hash) % defaultAvatars.length;
    return defaultAvatars[index];
};

const Label = styled(Typography)({
    display: 'inline-flex',
    position: 'relative',
    color: 'var(--state-color)',
    fontSize: '10px',
    fontWeight: 500,
    padding: '2px 8px',
    textTransform: 'capitalize',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        height: '18px',
        width: '100%',
        opacity: 0.2,
        borderRadius: '10px',
        backgroundColor: 'var(--state-color)',
    },
});

interface TournamentBoxProps {
    tournament: ITournamentState;
    userState: IUserState;
    handleClick: (gameId: string) => void;
}

export default function TournamentBox({ tournament, userState, handleClick }: TournamentBoxProps) {
    const players = Array.isArray(tournament.playerList) ? tournament.playerList : [];
    const maxAvatars = 4;
    const displayedPlayers = players.slice(0, maxAvatars);
    const isUserRegistered = isTournamentRegisteredByUser(tournament, userState.id);

    return (
        <Card 
            sx={{
                width: '206px',
                minWidth: '206px',
                maxWidth: '206px',
                flexShrink: 0,
                flexGrow: 0,
                position: 'relative',
                scrollSnapAlign: 'start',
                '&:hover .open-lobby-button': {
                    opacity: 1,
                    visibility: 'visible'
                }
            }}
            onClick={() => handleClick(tournament.gameId)}
        >
            <CardActionArea>
                <Box sx={{ p: 3, backgroundColor: '#1C1C25' }}>
                    <CardMedia
                        component="img"
                        alt="Tournament logo"
                        height="45"
                        image={tournament.imageName ? `${import.meta.env.BASE_URL}images/tournament/${tournament.imageName}` : defaultTourneyImg}
                        onError={(e) => {
                            e.currentTarget.src = defaultTourneyImg;
                        }}
                        style={{ objectFit: 'contain' }}
                    />
                </Box>
                <CardContent sx={{ backgroundColor: '#2A2A32', py: 1, px: "11px", '--state-color': (isLateRegistrationOpen(tournament) ? '#F93751' : getStateColor(tournament.status)) }}>
                    <Stack direction="row" justifyContent="space-between">
                        { tournament.status === 'registration' && isUserRegistered ? (
                            <Label sx={{ mr: 'auto' }}>{t('Tournament:box.registered')}</Label>
                        ) : (
                            <Label sx={{ mr: 'auto' }}>{(isLateRegistrationOpen(tournament) || tournament.status === 'late_registration') ? t('Tournament:status.lateRegistration') : (tournament.status === 'paused' ? t('Tournament:status.onBreak') : (tournament.status === 'running' ? t('Tournament:status.inPlay') : tournament.status))}</Label>
                        )}
                        {tournament.isSatellite && (
                            <Tooltip title={t('Tournament:tooltips.satellite')} placement="top" arrow>
                                <img src={satelliteIcon} alt="Satellite Icon" style={{ marginLeft: "5px" }} height={16} />
                            </Tooltip>
                        )}
                        {tournament.buyIn === 0 && (
                            <Tooltip title={t('Tournament:tooltips.freeroll')} placement="top" arrow>
                                <img src={freerollIcon} alt="Freeroll Icon" style={{ marginLeft: "5px" }} height={16} />
                            </Tooltip>
                        )}
                    </Stack>
                    <Typography title={tournament.name} sx={{ fontWeight: 600, color: '#FFFFFF', fontSize: '14px', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', my: 1 }}>
                        {tournament.name}
                    </Typography>
                    <Stack direction="row" justifyContent="space-between">
                        <Typography sx={{ fontSize: '11px', color: '#8695A6' }}>{t('Tournament:info.buyIn')}: {tournament.buyIn == 0 ? t('Tournament:gameTypes.freeroll') : `${formatCurrencySmartDecimal(tournament.buyIn)}${tournament.entryFee > 0 ? ` + ${formatCurrencySmartDecimal(tournament.entryFee)}` : ''}`}</Typography>
                        <Typography sx={{ fontSize: '11px', color: 'white', fontWeight: 'bold' }}>{t('Tournament:info.prize')}: {formatCurrencySmartDecimal(getEffectivePrize(tournament))}</Typography>
                    </Stack>
                </CardContent>
                <CardActions sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#222328', height: '24px' }}>
                    <Stack direction="row" alignItems="center">
                        <ScheduleIcon sx={{ fontSize: '14px', color: '#8695A6', marginRight: '4px' }} />
                        <Typography style={{ fontSize: '12px', lineHeight: '15px', fontWeight: 'bold', textTransform: 'capitalize' }}>{ getStartText(tournament) }</Typography>
                    </Stack>
                    <AvatarGroup 
                        max={maxAvatars}
                        total={players.length}
                        sx={{
                            '& .MuiAvatar-root': {
                                border: '2px solid #222328',
                                width: 19,
                                height: 19,
                                fontSize: '8px',
                                fontWeight: '600'
                            },
                            '& .MuiAvatarGroup-avatar': {
                                backgroundColor: '#4A4A52',
                                color: '#FFFFFF'
                            }
                        }}
                    >
                        {displayedPlayers.map((player) => (
                            <Avatar key={player.userId} alt={player.username} src={getDefaultAvatar(player.userId)}>{player.username.charAt(0)}</Avatar>
                        ))}
                    </AvatarGroup>
                </CardActions>
                <Box
                    className="open-lobby-button"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleClick(tournament.gameId);
                    }}
                    sx={{
                        position: 'absolute',
                        top: '46px',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        opacity: 0,
                        visibility: 'hidden',
                        transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out, transform 0.2s ease-in-out',
                        backgroundColor: '#F93751',
                        color: '#FFFFFF',
                        fontSize: '12px',
                        fontWeight: 600,
                        textTransform: 'none',
                        padding: '6px 16px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: 'none',
                        '&:hover': {
                            backgroundColor: '#F93751',
                            transform: 'translate(-50%, -50%) scale(1.05)'
                        }
                    }}
                >
                    {t('Tournament:actions.openLobby')}
                </Box>
            </CardActionArea>
        </Card>
    );
}
